import { z } from "zod";
import { FirestoreTimestampSchema } from "./common.schema";

/**
 * Scheduled jobs — `organizations/{org_id}/domains/{domain_id}/jobs/{job_id}`.
 *
 * NOT the same thing as `jobs.schema.ts`, which is heuristic *audit* jobs at
 * `.../pages/{page_id}/heuristic_jobs/{job_id}`. Everything here is prefixed
 * `ScheduledJob` so the two vocabularies cannot be confused at a call site.
 *
 * Mirrors core `schemas/firestore/scheduled_jobs.schema.json` and
 * `job_runs.schema.json` — `npm run schema:drift` compares them field by field.
 */

/** §10 risk 2 — a step is a whole agent turn and the run must fit the Cloud Tasks deadline. */
export const MAX_STEPS_PER_JOB = 6;
/** §7.1 guard 3. core answers 409 past this, per domain. */
export const MAX_JOBS_PER_DOMAIN = 20;

/**
 * core's step registry (`app/jobs/application/registry.py`), duplicated because
 * it is an in-code allowlist with no API that exposes it.
 *
 * This is real drift risk and `npm run schema:drift` will not catch it — the
 * registry is not a Firestore schema. A tool added there and not here is merely
 * absent from the picker; a tool removed there and left here is a job that
 * saves and then 400s. If you touch `STEP_REGISTRY`, touch this.
 */
type ToolArg = { name: string; label: string };

/** core's global schedule floor (`MIN_SCHEDULE_INTERVAL_SECONDS`) -- the
 * default `min_interval_seconds` for a tool with no tighter floor of its own. */
export const DEFAULT_MIN_INTERVAL_SECONDS = 3600;

export const REGISTERED_TOOLS = {
  wp_trigger_sync: {
    label: "Sync WordPress",
    description:
      "Pull the current state of a connected WordPress site into Crolytics.",
    args: [] as ToolArg[],
    min_interval_seconds: 86400,
  },
  analytics_snapshot_week: {
    label: "Snapshot analytics",
    description:
      "Store this week's GA4 and Search Console numbers for the domain, with the week-over-week diff.",
    args: [] as ToolArg[],
    min_interval_seconds: 604800,
  },
  heuristic_run_audit: {
    label: "Run a CRO audit",
    description:
      "Run a full heuristic audit of one page and store the recommendation.",
    args: [
      { name: "url", label: "Page URL" },
      { name: "page_id", label: "Crolytics page id" },
    ],
    min_interval_seconds: DEFAULT_MIN_INTERVAL_SECONDS,
  },
} as const;

export type RegisteredTool = keyof typeof REGISTERED_TOOLS;

export const JobScheduleSchema = z.object({
  cron: z.string(),
  timezone: z.string(),
  is_enabled: z.boolean().default(false),
});
export type JobSchedule = z.infer<typeof JobScheduleSchema>;

export const ToolStepSchema = z.object({
  type: z.literal("tool"),
  tool: z.string(),
  args: z.record(z.string(), z.string()).default({}),
});
export type ToolStep = z.infer<typeof ToolStepSchema>;

export const RecipeStepSchema = z.object({
  type: z.literal("recipe"),
  recipe_owner: z.enum(["system", "org"]),
  recipe_kind: z.string(),
  params: z.record(z.string(), z.string()).default({}),
});
export type RecipeStep = z.infer<typeof RecipeStepSchema>;

export const JobStepSchema = z.discriminatedUnion("type", [
  ToolStepSchema,
  RecipeStepSchema,
]);
export type JobStep = z.infer<typeof JobStepSchema>;

export const ScheduledJobSchema = z.object({
  job_id: z.string(),
  org_id: z.string(),
  domain_id: z.string(),
  owner_uid: z.string(),
  display_label: z.string(),
  steps: z.array(JobStepSchema),
  schedule: JobScheduleSchema.nullable().default(null),
  next_run_at: z.date().nullable().optional(),
  last_run_at: z.date().nullable().optional(),
  last_run_id: z.string().nullable().default(null),
  /**
   * An agent authored the *current* state of this job — not frozen at
   * creation. A human edit clears it, and that is exactly how a member arms a
   * schedule an agent drafted (§7.1 guard 2). The list has to say so, or a
   * job that will never fire looks identical to one that will.
   */
  is_created_by_agent: z.boolean().default(false),
  version: z.number().default(1),
  created_at: z.date(),
  updated_at: z.date(),
});

export const ScheduledJobEntitySchema = ScheduledJobSchema.extend({
  next_run_at: FirestoreTimestampSchema.optional(),
  last_run_at: FirestoreTimestampSchema.optional(),
  created_at: FirestoreTimestampSchema.optional(),
  updated_at: FirestoreTimestampSchema.optional(),
});
export type ScheduledJob = z.infer<typeof ScheduledJobEntitySchema>;

export const StepStatusSchema = z.enum([
  "pending",
  "running",
  "completed",
  "failed",
  "skipped",
]);
export type StepStatus = z.infer<typeof StepStatusSchema>;

export const RunStatusSchema = z.enum(["running", "succeeded", "failed"]);
export type RunStatus = z.infer<typeof RunStatusSchema>;

export const StepResultSchema = z.object({
  index: z.number(),
  type: z.enum(["tool", "recipe"]),
  status: StepStatusSchema,
  output: z.string().nullable().default(null),
  output_uri: z.string().nullable().default(null),
  is_output_truncated: z.boolean().default(false),
  /** Recipe steps only — the conversation the run produced. */
  thread_id: z.string().nullable().default(null),
  error: z.string().nullable().default(null),
  attempts: z.number().default(0),
  started_at: z.date().nullable().optional(),
  finished_at: z.date().nullable().optional(),
});
export type StepResult = z.infer<typeof StepResultSchema>;

export const ScheduledJobRunSchema = z.object({
  run_id: z.string(),
  job_id: z.string(),
  org_id: z.string(),
  domain_id: z.string(),
  owner_uid: z.string(),
  trigger: z.enum(["schedule", "manual"]),
  triggered_by_uid: z.string().nullable().default(null),
  status: RunStatusSchema,
  current_step: z.number().default(0),
  steps: z.array(StepResultSchema).default([]),
  started_at: z.date(),
  finished_at: z.date().nullable().optional(),
  expires_at: z.date(),
});

export const ScheduledJobRunEntitySchema = ScheduledJobRunSchema.extend({
  steps: z
    .array(
      StepResultSchema.extend({
        started_at: FirestoreTimestampSchema.optional(),
        finished_at: FirestoreTimestampSchema.optional(),
      }),
    )
    .default([]),
  started_at: FirestoreTimestampSchema.optional(),
  finished_at: FirestoreTimestampSchema.optional(),
  expires_at: FirestoreTimestampSchema.optional(),
});
export type ScheduledJobRun = z.infer<typeof ScheduledJobRunEntitySchema>;

/**
 * A step as it comes back off a run document — Firestore `Timestamp`s, not the
 * `Date`s the drift-compared base schema declares. Derived from the entity so
 * the two can never disagree.
 */
export type ScheduledJobStepResult = ScheduledJobRun["steps"][number];

export const safeParseScheduledJob = (data: unknown) =>
  ScheduledJobEntitySchema.safeParse(data);

export const safeParseScheduledJobRun = (data: unknown) =>
  ScheduledJobRunEntitySchema.safeParse(data);
